import { orderBy, snakeCase } from 'lodash';
import moment from "moment";
import React, { useContext, useEffect, useRef } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { FaCheckCircle, FaChevronLeft, FaChevronRight, FaDownload, FaExclamationTriangle, FaFileExport, FaMinusCircle, FaPlus, FaSpinner, FaTimes, FaTimesCircle } from "react-icons/fa";
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from "styled-components";
import { fetchGroups } from '../common/slices/group';
import { selectActive } from "../common/slices/location";
import { fetchTimeEntriesByCrewMemberId, fetchTimeEntriesByCustomerId, fetchTimeEntriesByGroups, fetchTimeEntriesByLocationId, fetchTimeEntriesByScopeId, fetchTimeEntriesByStatus, fetchTimeEntryReport, resetTEPagination, selectLoading, selectTEPagination, selectTimeEntries, setTimeEntries } from "../common/slices/timesheet";
import { ErrorContext } from "../ErrorContext";
import formatCurrency from "../utils/formatCurrency";
import { BlackButton, ButtonBase, WhiteButton } from "./Buttons";
import Divider from "./Divider";
import FormDatePicker from "./FormDatePicker";
import FormSelectCrewMember from "./FormSelectCrewMember";
import FormSelectCustomer from "./FormSelectCustomer";
import FormSelectGroup from "./FormSelectGroup";
import FormSelectInput from "./FormSelectInput";
import FormSelectScopeOfWork from "./FormSelectScopeOfWork";
import Modal from "./Modal";
import Row from "./Row";
import { Table, TableData, TableFooter, TableHeader, TableRow } from "./Table";
import { H1 } from "./Typography";

const Footer = styled.tfoot`
    td {
        border: none;
        font-weight: 700;
    }
`

const RowDeleteButton = (props) => (
    <ButtonBase {...props} style={{ padding: '1rem' }} />
)

const defaultFilters = [
    { label: 'Groups', value: 'groupId' },
    { label: 'PO Number', value: 'customerId' },
    { label: 'Scope of Work', value: 'scopeId' },
    { label: 'Team Member', value: 'crewMemberId' },
]

const TimeEntryReportFilter = ({ name, item, index, onDelete, ...formProps }) => {
    const filterType = useWatch({ control: formProps.control, name: `${name}.${index}.filterType` })

    return (
        <Row key={item.keyId}>
            <RowDeleteButton onClick={onDelete}><FaMinusCircle color="#000" /></RowDeleteButton>
            {/* <FormSelectInput containerStyle={{ flex: 0, minWidth: '8rem' }} name={`${name}.${index}.logic`}  {...formProps} defaultOptions={[
                { label: 'And', value: 'AND' },
                { label: 'Or', value: 'OR' },
            ]} rules={{ required: true }} /> */}
            <FormSelectInput name={`${name}.${index}.filterType`} {...formProps} defaultOptions={defaultFilters} rules={{ required: true }} />
            {filterType?.value === "groupId" && <FormSelectGroup name={`${name}.${index}.values`} {...formProps} isMulti containerStyle={{ flex: 3 }} includeDivisions />}
            {filterType?.value === "crewMemberId" && <FormSelectCrewMember name={`${name}.${index}.values`} {...formProps} isMulti showAll containerStyle={{ flex: 3 }} />}
            {filterType?.value === "scopeId" && <FormSelectScopeOfWork name={`${name}.${index}.values`} {...formProps} isMulti containerStyle={{ flex: 3 }} />}
            {filterType?.value === "customerId" && <FormSelectCustomer name={`${name}.${index}.values`} {...formProps} isMulti containerStyle={{ flex: 3 }} />}
        </Row>
    );
}

const TimeEntryReportFilters = ({ control, name, errors, rules, locationId }) => {
    const { fields, append, remove } = useFieldArray({
        rules,
        control,
        name,
        keyName: "keyId",
    })

    const formProps = { control, errors, locationId, remove }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            {
                fields.map((item, index) => (
                    <TimeEntryReportFilter name={name} item={item} index={index} {...formProps} onDelete={() => remove(index)} />
                ))
            }
            <WhiteButton onClick={() => append({
                logic: { value: 'AND', label: 'And' }
            })}><FaPlus /> Add Filter</WhiteButton>
        </div>
    )
}

const generateFilter = async ({ filters }, dispatch) => {
    let returnValue = await filters.reduce(async (resultP, filter) => {
        let result = await resultP
        let newFilter

        switch (filter.filterType.value) {
            case "crewMemberId":
            case "customerId":
            case "scopeId":
                newFilter = [{ or: filter.values.map(f => ({ [filter.filterType.value]: { eq: f.value } })) }]
                break;
            case "groupId":
                const groups = await dispatch(fetchGroups(filter.values.map(f => f.value)))
                newFilter = groups.reduce((f, group) => {
                    group.crewMemberIds.map(cm => f.push({ crewMemberId: { eq: cm } }))
                    return f;
                }, []).slice(0, 50) //Prevent huge groups from crashing the query
                break;
        }

        if (filter.logic.value === "AND" && filter.filterType.value !== 'groupId') {
            return {
                and: [...result.and, ...newFilter],
                or: [...result.or]
            }
        } else {
            return {
                and: [...result.and],
                or: [...result.or, ...newFilter]
            }
        }
    }, Promise.resolve({ and: [], or: [] }))

    if (!returnValue.and.length) delete returnValue.and;
    if (!returnValue.or.length) delete returnValue.or;

    return returnValue;
}

const ModalTimesheetReport = ({ onClose }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const locationId = useSelector(selectActive);
    const timeEntries = useSelector(selectTimeEntries);
    const pagination = useSelector(selectTEPagination);
    const loading = useSelector(selectLoading);
    const modalRef = useRef();

    const {
        control,
        watch,
        setValue,
        formState: { errors, isValid, isSubmitting },
        handleSubmit,
    } = useForm({
        defaultValues: {
            startDate: moment().add(-1, 'day').toDate(),
            endDate: moment().endOf('day').toDate(),
        }
    })

    const startDate = watch("startDate");
    const endDate = watch("endDate");
    const reportType = watch("reportType");

    useEffect(() => {
        dispatch(setTimeEntries({ items: [], nextToken: null }))
        return () => dispatch(setTimeEntries({ items: [], nextToken: null }))
    }, [])


    useEffect(() => {
        setValue("filters", [])
    }, [reportType])

    const totals = timeEntries?.reduce((totals, itr, idx) => {
        const straightTime = itr.adjustedHours.straightTime
        const overtime = itr.adjustedHours.overtime
        const rate = itr.crewMember?.rateOverride?.rate || itr.crewMember?.rate
        const jobCost = (straightTime * rate) + (overtime * 1.5 * rate)

        return {
            hours: totals.hours + itr.hours,
            straightTime: totals.straightTime + straightTime,
            overtime: totals.overtime + overtime,
            jobCost: totals.jobCost + jobCost,
            rate: totals.rate + (rate),
        }
    }, { hours: 0, straightTime: 0, overtime: 0, jobCost: 0, rate: 0 })

    const formProps = { control, errors, locationId };

    const dispatcher = {
        byCustomer: (params, filter, nextToken, storeNextToken) => dispatch(fetchTimeEntriesByCustomerId(params.customerId.value, locationId, params.startDate, params.endDate, filter, nextToken, storeNextToken)),
        byLocation: (params, filter, nextToken, storeNextToken) => dispatch(fetchTimeEntriesByLocationId(locationId, params.startDate, params.endDate, filter, nextToken, storeNextToken)),
        byCrewMember: (params, filter, nextToken, storeNextToken) => dispatch(fetchTimeEntriesByCrewMemberId(params.crewMemberId.value, locationId, params.startDate, params.endDate, filter, nextToken, storeNextToken)),
        byScope: (params, filter, nextToken, storeNextToken) => dispatch(fetchTimeEntriesByScopeId(params.scopeId.value, locationId, params.startDate, params.endDate, filter, nextToken, storeNextToken)),
        byStatus: (params, filter, nextToken, storeNextToken) => dispatch(fetchTimeEntriesByStatus(locationId, params.status.value, undefined, { date: { between: [params.startDate, params.endDate] } }, nextToken, storeNextToken)),
        byGroups: (params, filter, nextToken, storeNextToken) => dispatch(fetchTimeEntriesByGroups(locationId, params.groupIds.map(g => g.value), params.startDate, params.endDate, filter, nextToken, storeNextToken)),
    }

    const onGenerate = async (params) => {
        try {
            const filter = await generateFilter(params, dispatch);
            dispatch(resetTEPagination());
            await dispatcher[params.reportType.value](params, filter);
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to generate report.", forSupport: ex.message })
        }
    }

    const onDownloadReport = (PDF) => async (params) => {
        try {
            const marshall = (params) => ({
                locationId,
                ...params,
                startDate: moment(params.startDate).startOf('day'),
                endDate: moment(params.endDate).endOf('day'),
                reportType: params.reportType?.value,
                scopeId: params.scopeId?.value,
                status: params.status?.value,
                crewMemberId: params.crewMemberId?.value,
                customerId: params.customerId?.value,
                groupIds: params.groupIds?.map(g => g.value),
                PDF
            })

            await dispatch(fetchTimeEntryReport({ ...marshall(params), locationId }));
            toast.success("Time entry report generation in progress.  You will be notified when it is completed.")
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to generate time entry report", forSupport: ex.message })
        }
    }

    const onNextPage = async (params) => {
        try {
            const filter = await generateFilter(params, dispatch);
            await dispatcher[params.reportType.value](params, filter, pagination.nextToken);
            modalRef.current.scrollTo({ top: 0 })
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to fetch next page from server", forSupport: ex.message })
        }
    }

    const onPrevPage = async (params) => {
        try {
            const filter = await generateFilter(params, dispatch);
            await dispatcher[params.reportType.value](params, filter, pagination.previousTokens[pagination.previousTokens.length - 2], false);
            modalRef.current.scrollTo({ top: 0 })
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to fetch previous page from server", forSupport: ex.message })
        }
    }

    const onExportTable = () => {
        try {
            const output = orderBy(timeEntries, ['date'], 'desc').reduce((out, te) => {
                const data = [moment(te.date).format('MMM DD YYYY'), te.crewMember.name, te.scope.name, te.hours, te.adjustedHours.straightTime, te.adjustedHours.overtime];
                return [...out, data.join(',')]
            }, [])

            output.unshift("Date, Crew Member, Scope of Work, Hours, Straight Time, Overtime")

            const link = document.createElement("a");
            const content = output.join('\n');
            const file = new Blob([content], { type: 'text/plain' });
            link.href = URL.createObjectURL(file);
            link.download = `${snakeCase(reportType.value)}_${moment(startDate).format('MM-DD-YYYY')}_${moment(endDate).format('MM-DD-YYYY')}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to export table", forSupport: ex.message })
        }
    }




    return (
        <Modal show size="85%" ref={modalRef}>

            <Row>
                <H1>Generate Report</H1>
                <ButtonBase onClick={onClose} style={{ marginLeft: 'auto' }} disabled={loading}><FaTimes size='16px' color="#000" /></ButtonBase>
            </Row>

            <div>
                <Row>
                    <FormDatePicker {...formProps} name="startDate" label="Start Date" startDate={startDate} endDate={endDate} maxDate={endDate} />
                    <FormDatePicker {...formProps} name="endDate" label="End Date" startDate={startDate} endDate={endDate} minDate={startDate} />
                </Row>

                <Row>
                    <FormSelectInput name="reportType" {...formProps} label="Report by" defaultOptions={[
                        { label: 'Location', value: 'byLocation' },
                        { label: 'PO Number', value: 'byCustomer' },
                        { label: 'Scope of Work', value: 'byScope' },
                        { label: 'Sync Status', value: 'byStatus' },
                        { label: 'Team Member', value: 'byCrewMember' },
                    ]} rules={{ required: true }} />
                    {reportType?.value === 'byCustomer' && <FormSelectCustomer name="customerId" {...formProps} label="PO Number" rules={{ required: true }} />}
                    {reportType?.value === 'byCrewMember' && <FormSelectCrewMember showAll name="crewMemberId" {...formProps} label="Crew Member" rules={{ required: true }} />}
                    {reportType?.value === 'byScope' && <FormSelectScopeOfWork name="scopeId" {...formProps} label="Scope of Work" rules={{ required: true }} />}
                    {reportType?.value === 'byStatus' && <FormSelectInput name="status" {...formProps} label="Status" defaultOptions={[
                        { label: 'Error', value: 'ERROR' },
                        { label: 'Pending', value: 'PENDING' },
                        { label: 'Sync Completed', value: 'SYNC_COMPLETE' },
                        { label: 'Sync Started', value: 'SYNC_STARTED' },
                    ]} rules={{ required: true }} />}
                    {reportType?.value === 'byGroups' && <FormSelectGroup isMulti name="groupIds" {...formProps} label="Groups" rules={{ required: true }} includeDivisions />}
                </Row>
                {['byStatus'].indexOf(reportType?.value) < 0 && <TimeEntryReportFilters name="filters" {...formProps} />}
            </div>

            <Row>
                <BlackButton onClick={handleSubmit(onGenerate)} disabled={isSubmitting || !isValid || loading} style={{ alignSelf: 'flex-start' }}>Preview</BlackButton>
                <BlackButton onClick={handleSubmit(onDownloadReport(false))} disabled={isSubmitting || !isValid || loading} style={{ alignSelf: 'flex-start' }}>Download Job Cost <FaDownload /> </BlackButton>
                <BlackButton onClick={handleSubmit(onDownloadReport(true))} disabled={isSubmitting || !isValid || loading} style={{ alignSelf: 'flex-start' }}>Download Payroll Report <FaDownload /> </BlackButton>
                <div style={{ flex: 1 }} />
                <ButtonBase onClick={handleSubmit(onExportTable)} disabled={isSubmitting || !isValid || loading}><FaFileExport /></ButtonBase>
            </Row>

            {
                <>
                    <Divider.Horizontal />

                    <Table>
                        <thead>
                            <tr>
                                <TableHeader colSpan={2}>Date</TableHeader>
                                <TableHeader>Customer</TableHeader>
                                <TableHeader>Crew Member</TableHeader>
                                <TableHeader>Scope of Work</TableHeader>
                                <TableHeader>Rate /hr</TableHeader>
                                <TableHeader>Hours</TableHeader>
                                <TableHeader>Straight Time</TableHeader>
                                <TableHeader>Overtime</TableHeader>
                                <TableHeader>Cost</TableHeader>
                            </tr>
                        </thead>

                        <tbody>
                            {orderBy(timeEntries, 'date', 'desc').map((timeEntry) => {
                                const straightTime = timeEntry.adjustedHours.straightTime
                                const overtime = timeEntry.adjustedHours.overtime
                                const rate = timeEntry.crewMember?.rateOverride?.rate || timeEntry.crewMember?.rate
                                const jobCost = (straightTime * rate) + (overtime * 1.5 * rate)

                                return (
                                    <TableRow key={timeEntry.id}>
                                        <TableData style={{ width: '1rem' }}>{timeEntry.isApproved ? <FaCheckCircle title='Approved' /> : <FaExclamationTriangle title='Not Approved' />}</TableData>
                                        <TableData>{moment(timeEntry.date).format('MMM D, YYYY')}</TableData>
                                        <TableData>{timeEntry.customer?.name}</TableData>
                                        <TableData>{timeEntry.crewMember?.active ? '' : <FaTimesCircle title="Inactive User" style={{ marginRight: '0.25rem' }} />}{timeEntry.crewMember?.name}</TableData>
                                        <TableData>{timeEntry.scope?.name}</TableData>
                                        <TableData>{formatCurrency(rate)}</TableData>
                                        <TableData>{moment.duration({ hours: timeEntry.hours }).format("*h:mm [h]")}</TableData>
                                        <TableData>{moment.duration({ hours: straightTime }).format("*h:mm [h]")}</TableData>
                                        <TableData>{moment.duration({ hours: overtime }).format("*h:mm [h]")}</TableData>
                                        <TableData>{formatCurrency(jobCost)}</TableData>
                                    </TableRow>
                                )
                            })}
                        </tbody>

                        <Footer>
                            <TableRow>
                                <TableData colSpan={5}></TableData>
                                <TableData>{formatCurrency(totals?.rate)}</TableData>
                                <TableData>{moment.duration({ hours: totals?.hours }).format("*h:mm [h]")}</TableData>
                                <TableData>{moment.duration({ hours: totals?.straightTime }).format("*h:mm [h]")}</TableData>
                                <TableData>{moment.duration({ hours: totals?.overtime }).format("*h:mm [h]")}</TableData>
                                <TableData>{formatCurrency(totals?.jobCost)}</TableData>
                            </TableRow>
                        </Footer>
                    </Table>
                    <TableFooter>
                        {loading && <FaSpinner className="fa-spin" />}
                        <ButtonBase onClick={handleSubmit(onPrevPage)} disabled={!pagination.previousTokens[pagination.previousTokens.length - 1] || loading} style={{ color: 'black' }}><FaChevronLeft /></ButtonBase>
                        <ButtonBase onClick={handleSubmit(onNextPage)} disabled={!pagination.nextToken || loading} style={{ color: 'black' }}><FaChevronRight /></ButtonBase>
                    </TableFooter>
                </>
            }
        </Modal>
    );
};

export default ModalTimesheetReport;
